import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { useBootstrapTheme } from "../../contexts/BootstrapThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faX } from "@fortawesome/free-solid-svg-icons";
import { EffectThemeDropdownItemProps, EffectThemeDropdownMenuProps, EffectThemeDropdownProps, EffectThemeDropdownToggleProps, EffectThemeSearchFilterDropdownProps, Variant } from "../../types/effectTheme";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";



export const useEffectThemeStyles = (variant: Variant = "primary") => {
  const { effectiveTheme } = useBootstrapTheme();
  const isDark = effectiveTheme === "dark";

  const toggleBase: CSSProperties = {
    width: "100%",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
    padding: "0.55rem 0.9rem",
    borderRadius: "0.6rem",
    backdropFilter: "blur(10px)",
    transition: "all 0.25s ease",
    border: isDark
      ? "1px solid rgba(96, 165, 250, 0.28)"
      : "1px solid rgba(59, 130, 246, 0.18)",
    boxShadow: isDark
      ? "0 6px 22px rgba(59, 130, 246, 0.10), 0 2px 8px rgba(96, 165, 250, 0.05)"
      : "0 6px 22px rgba(2, 6, 23, 0.06), 0 2px 8px rgba(0, 0, 0, 0.03)",
  };

  const toggleVariant: CSSProperties =
    variant === "primary"
      ? {
          color: isDark ? "rgba(226,232,240,0.95)" : "rgba(15,23,42,0.95)",
          background: isDark
            ? "linear-gradient(145deg, rgba(59,130,246,0.22), rgba(37,99,235,0.30))"
            : "linear-gradient(145deg, rgba(219,234,254,0.92), rgba(191,219,254,0.88))",
        }
      : {
          color: isDark ? "rgba(226,232,240,0.92)" : "rgba(15,23,42,0.92)",
          background: isDark
            ? "linear-gradient(145deg, rgba(30,41,59,0.80), rgba(15,23,42,0.88))"
            : "linear-gradient(145deg, rgba(248,250,252,0.95), rgba(241,245,249,0.90))",
        };

  const menuStyle: CSSProperties = {
    width: "100%",
    padding: "0.4rem",
    borderRadius: "0.75rem",
    backdropFilter: "blur(12px)",
    border: isDark
      ? "1px solid rgba(96, 165, 250, 0.22)"
      : "1px solid rgba(59, 130, 246, 0.16)",
    background: isDark
      ? "linear-gradient(145deg, rgba(20, 38, 80, 0.96) 0%, rgba(15, 23, 42, 0.98) 55%, rgba(30, 41, 59, 0.96) 100%)"
      : "linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.99) 55%, rgba(241,245,249,0.98) 100%)",
    boxShadow: isDark
      ? "0 14px 40px rgba(59,130,246,0.12)"
      : "0 14px 40px rgba(2,6,23,0.10)",
    zIndex: 200,
  };

  const itemBase: CSSProperties = {
    borderRadius: "0.55rem",
    padding: "0.55rem 0.9rem",
    transition: "all 0.2s ease",
    color: isDark ? "rgba(226,232,240,0.95)" : "rgba(15,23,42,0.95)",
  };

  const itemHoverBg = isDark
    ? "rgba(96, 165, 250, 0.16)"
    : "rgba(59, 130, 246, 0.10)";

  const itemActiveBg = isDark
    ? "rgba(59, 130, 246, 0.22)"
    : "rgba(59, 130, 246, 0.16)";

  return {
    effectiveTheme,
    isDark,
    toggleStyle: { ...toggleBase, ...toggleVariant },
    menuStyle,
    itemBase,
    itemHoverBg,
    itemActiveBg,
  };
};

const EffectThemeDropdown = ({
  className,
  title,
  children,
  drop = 'down',
  style,
}: EffectThemeDropdownProps) => {
  return (
    <Dropdown
      className={className}
      title={title}
      drop={drop}
      style={style}
    >
      {children}
    </Dropdown>
  );
};

const EffectThemeDropdownToggle = ({
  id,
  className,
  title="",
  style,
  variant = "secondary",
  children,
  disabled
}: EffectThemeDropdownToggleProps) => {
  const { toggleStyle, isDark } = useEffectThemeStyles(variant);

  return (
    <Dropdown.Toggle
      id={id}
      // variant bleibt "neutral", wir stylen selbst
      variant={isDark ? "dark" : "light"}
      className={className}
      style={{ ...toggleStyle, ...style }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
      }}
      title={title}
      disabled={disabled}
    >
      <span className="text-truncate">
        {children}
      </span>
    </Dropdown.Toggle>
  );
};

const EffectThemeDropdownMenu = ({ className, style, children, maxHeight = 260 }: EffectThemeDropdownMenuProps) => {
  const { menuStyle } = useEffectThemeStyles();

  return (
    <>
      {
        createPortal(
          <Dropdown.Menu
            className={className}
            style={{
              ...menuStyle,
              zIndex: 200,
              maxHeight,
              overflowY: "auto",
              overflowX: "hidden",
              maxWidth: 350,
              ...style,
            }}
          >
            {children}
          </Dropdown.Menu>,
          document.body
        )
      }
    </>
  );
};

const EffectThemeDropdownItem = ({
  className,
  style,
  active,
  onClick,
  children,
  disabled
}: EffectThemeDropdownItemProps) => {
  const { itemBase, itemHoverBg, itemActiveBg } = useEffectThemeStyles();

  return (
    <Dropdown.Item
      onClick={(event) => {
        if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        onClick?.();
      }}
      className={className}
      style={{
        ...itemBase,
        background: active
            ? itemActiveBg
            : "transparent",
        cursor: disabled
            ? "arrow"
            : "pointer",
        opacity: disabled
            ? 0.55
            : 1,
        ...style,
    }}
      onMouseEnter={(event) => {
          if (disabled) return;
          event.currentTarget.style.background = itemHoverBg;
      }}
      onMouseLeave={(event) => {
          if (disabled) return;
          event.currentTarget.style.background = active
            ? itemActiveBg
            : "transparent";
      }}
      disabled={disabled}
    >
      {children}
    </Dropdown.Item>
  );
};

const EffectThemeSearchFilterDropdown = <T,>({
    id,
    className,
    toggleClassName = "w-100",
    menuClassName = "p-2 w-100",
    selectedLabel,
    items,
    selectedItem = null,
    onSelect,
    getItemKey,
    getItemSearchText,
    renderItem,
    filterGroups = [],
    searchPlaceholder = "Search...",
    resetKey = null,
    maxHeight = 420,
    style
}: EffectThemeSearchFilterDropdownProps<T>) => {
    const [search, setSearch] = useState("");
    const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
    const [filtersOpen, setFiltersOpen] = useState(false);
    const {t} = useTranslation('imageStudioSettings');


    useEffect(() => {
      setSearch("");
      setActiveFilters({});
      setFiltersOpen(false);
  }, [resetKey]);

    const toggleTag = (groupName: string, tag: string) => {
        setActiveFilters(prev => {
            const current = prev[groupName] ?? [];
            const next = current.includes(tag)
                ? current.filter(item => item !== tag)
                : [...current, tag];

            return {
                ...prev,
                [groupName]: next
            };
        });
    };

    const clearFilters = () => {
        setSearch("");
        setActiveFilters({});
    };

    const filteredItems = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return items.filter(item => {
            const searchableText = [
                getItemSearchText(item),
                ...filterGroups.flatMap(g => g.getItemTags(item))
            ].join(" ").toLowerCase();
            const matchesSearch =
                normalizedSearch.length === 0 ||
                searchableText.includes(normalizedSearch);

            if (!matchesSearch) return false;

            return filterGroups.every(group => {
                const selectedTags = activeFilters[group.name] ?? [];
                if (selectedTags.length === 0) return true;

                const itemTags = group.getItemTags(item);
                return selectedTags.some(tag => itemTags.includes(tag));
            });
        });
    }, [items, search, activeFilters, filterGroups, getItemSearchText]);

    return (
        <EffectThemeDropdown className={className} drop="down">
            <EffectThemeDropdown.Toggle id={id} className={toggleClassName}>
                {selectedLabel}
            </EffectThemeDropdown.Toggle>

            <EffectThemeDropdown.Menu
                className={menuClassName}
                maxHeight={maxHeight}
                style={style}
            >
                <div
                    className="mb-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    <input
                        className="form-control form-control-sm"
                        value={search}
                        placeholder={searchPlaceholder}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {filterGroups.length > 0 && (
                  <div
                      className="mb-2"
                      onClick={(e) => e.stopPropagation()}
                  >
                      <button
                          type="button"
                          className="btn btn-sm border-0 w-100 d-flex justify-content-between align-items-center"
                          onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFiltersOpen(prev => !prev);
                          }}
                      >
                          <span className="fw-bold">Filter</span>
                          <span>{filtersOpen ?
                            <FontAwesomeIcon size="sm" icon={faChevronUp}/>
                            : <FontAwesomeIcon size="sm" icon={faChevronDown}/>}
                          </span>
                      </button>

                      {filtersOpen && (
                          <div className="mt-0 mx-2">
                              {filterGroups.map(group => (
                                  <div
                                      key={group.name}
                                      className="mb-2"
                                  >
                                      <div className="text-muted small mb-1">{group.name}</div>

                                      <div className="d-flex flex-wrap gap-1">
                                          {group.tags.map(tag => {
                                              const checked = (activeFilters[group.name] ?? []).includes(tag);

                                              return (
                                                  <label
                                                      key={tag}
                                                      className={`small px-2 py-1 rounded border ${checked ? "fw-bold" : ""}`}
                                                      style={{ cursor: "pointer" }}
                                                  >
                                                      <input
                                                          type="checkbox"
                                                          className="me-1"
                                                          checked={checked}
                                                          onChange={() => toggleTag(group.name, tag)}
                                                      />
                                                      {tag}
                                                  </label>
                                              );
                                          })}
                                      </div>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              )}

                {(search || Object.values(activeFilters).some(x => x.length > 0)) && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary w-100 mb-2"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearFilters();
                        }}
                    >
                      <FontAwesomeIcon className="me-2" icon={faX}/> {t("styles.clear")}
                    </button>
                )}

                {filteredItems.length > 0 ? (
                    filteredItems.map(item => {
                        const active = selectedItem
                            ? getItemKey(selectedItem) === getItemKey(item)
                            : false;

                        return (
                            <EffectThemeDropdown.Item
                                key={getItemKey(item)}
                                active={active}
                                onClick={() => onSelect(item)}
                            >
                                {renderItem(item, active)}
                            </EffectThemeDropdown.Item>
                        );
                    })
                ) : (
                    <div className="small opacity-75 px-2 py-2">
                        {t("styles.noMatch")}
                    </div>
                )}
            </EffectThemeDropdown.Menu>
        </EffectThemeDropdown>
    );
};

EffectThemeDropdown.Toggle = EffectThemeDropdownToggle;
EffectThemeDropdown.Menu = EffectThemeDropdownMenu;
EffectThemeDropdown.Item = EffectThemeDropdownItem;
EffectThemeDropdown.SearchFilter = EffectThemeSearchFilterDropdown;

export default EffectThemeDropdown;
